import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { typesForCategory } from "../utils/constants";

const DEFAULT_DELAY = 700;

/** Stable key for a row, whether it has been saved yet or not. */
export const itemKey = (item) => item?.id ?? item?.tempId;

const numeric = (val) => {
  if (val === null || val === undefined || String(val).trim() === "") return null;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? null : parsed;
};

/** Normalise the decimal fields the API returns as strings. */
export const processActionItems = (items = []) =>
  items.map((item) => ({
    ...item,
    ai_time_estimate: numeric(item.ai_time_estimate),
    time_taken: numeric(item.time_taken),
  }));

/**
 * Owns a task's action-item list: local state plus the create/update/delete
 * traffic behind it.
 *
 * Replaces the create flow that produced duplicate rows holding fragments of
 * the same sentence. That flow re-fired a debounced POST on every keystroke and
 * only reconciled the result by `tempId`; once the first POST returned, the row
 * no longer carried that `tempId`, so nothing matched, the draft was never
 * retired, and each subsequent keystroke created *another* row — each one saved
 * with however much of the description had been typed at that instant, all
 * sharing the draft's line-up number.
 *
 * Here each draft is created exactly once. A `creating` guard keyed by `tempId`
 * makes the POST idempotent, the request body is read from current state at
 * send time (so the full text is saved, not a stale prefix), and edits typed
 * while the POST is in flight are replayed as a PATCH the moment the id lands.
 *
 * @param {number|string|null} taskId Null until the task exists (create dialog).
 * @param {Object} [options]
 * @param {number} [options.workspaceId]
 * @param {(pending: boolean) => void} [options.onPendingChange]
 * @param {number} [options.delay]
 */
export default function useActionItems(taskId, options = {}) {
  const { workspaceId, onPendingChange, delay = DEFAULT_DELAY } = options;

  const [items, setItemsState] = useState([]);

  // Mirrors `items` so timers and async continuations can read the live list
  // without being re-armed on every render. Written through `commit` only.
  const itemsRef = useRef(items);

  const cfgRef = useRef({});
  cfgRef.current = { taskId, workspaceId, onPendingChange };

  // tempId -> true while its POST is in flight. Keeps creation idempotent.
  const creatingRef = useRef(new Set());
  // tempId -> pending create timer.
  const createTimersRef = useRef(new Map());
  // id -> { timer, updates } for saved rows.
  const updateTimersRef = useRef(new Map());
  // tempId -> fields edited while its POST was in flight, replayed afterwards.
  const postCreateEditsRef = useRef(new Map());

  /** Single write path, so `itemsRef` can never drift from React state. */
  const commit = useCallback((updater) => {
    const next =
      typeof updater === "function" ? updater(itemsRef.current) : updater;
    itemsRef.current = next;
    setItemsState(next);
    return next;
  }, []);

  const setPending = useCallback((value) => {
    cfgRef.current.onPendingChange?.(value);
  }, []);

  const idle = useCallback(
    () =>
      creatingRef.current.size === 0 &&
      createTimersRef.current.size === 0 &&
      updateTimersRef.current.size === 0,
    []
  );

  // --- saved rows ---------------------------------------------------------

  const flushUpdate = useCallback(
    async (id) => {
      const entry = updateTimersRef.current.get(id);
      if (!entry) return;
      updateTimersRef.current.delete(id);
      clearTimeout(entry.timer);
      const updates = entry.updates;
      if (Object.keys(updates).length === 0) return;

      try {
        const response = await api.patch(`action-items/${id}/`, updates);
        const saved = processActionItems([response.data])[0];
        // Reconcile server-side coercions (decimals come back as strings)
        // without stomping fields the user has kept editing since.
        const stillPending = updateTimersRef.current.get(id)?.updates || {};
        commit((prev) =>
          prev.map((item) => {
            if (item.id !== id) return item;
            const merged = { ...item, ...saved };
            Object.keys(stillPending).forEach((f) => {
              merged[f] = item[f];
            });
            return merged;
          })
        );
      } catch (error) {
        console.error("Error updating action item:", error, { id, updates });
      } finally {
        if (idle()) setPending(false);
      }
    },
    [commit, idle, setPending]
  );

  const scheduleUpdate = useCallback(
    (id, field, value) => {
      const existing = updateTimersRef.current.get(id);
      if (existing) clearTimeout(existing.timer);
      const updates = { ...(existing?.updates || {}), [field]: value };
      const timer = setTimeout(() => flushUpdate(id), delay);
      updateTimersRef.current.set(id, { timer, updates });
      setPending(true);
    },
    [delay, flushUpdate, setPending]
  );

  // --- drafts -------------------------------------------------------------

  const flushCreate = useCallback(
    async (tempId) => {
      createTimersRef.current.delete(tempId);
      // One POST per draft, ever. Without this guard every keystroke that
      // landed while a POST was in flight started another one.
      if (creatingRef.current.has(tempId)) return;

      const draft = itemsRef.current.find((item) => item.tempId === tempId);
      const { taskId: task } = cfgRef.current;
      if (!draft || !task) return;
      if (!String(draft.description || "").trim()) return;

      creatingRef.current.add(tempId);
      setPending(true);

      // Body read from *current* state, so the full description is saved rather
      // than whatever prefix existed when the timer was armed.
      const { tempId: _draftKey, ...payload } = draft;
      try {
        const response = await api.post("action-items/", {
          ...payload,
          task,
        });
        const saved = processActionItems([response.data])[0];
        commit((prev) =>
          prev.map((item) => (item.tempId === tempId ? saved : item))
        );

        // Replay anything typed while the POST was in flight.
        const queued = postCreateEditsRef.current.get(tempId);
        postCreateEditsRef.current.delete(tempId);
        if (queued) {
          Object.entries(queued).forEach(([field, value]) =>
            scheduleUpdate(saved.id, field, value)
          );
        }
      } catch (error) {
        console.error("Error creating action item:", error);
        commit((prev) => prev.filter((item) => item.tempId !== tempId));
        postCreateEditsRef.current.delete(tempId);
      } finally {
        creatingRef.current.delete(tempId);
        if (idle()) setPending(false);
      }
    },
    [commit, idle, scheduleUpdate, setPending]
  );

  const scheduleCreate = useCallback(
    (tempId) => {
      const existing = createTimersRef.current.get(tempId);
      if (existing) clearTimeout(existing);
      createTimersRef.current.set(
        tempId,
        setTimeout(() => flushCreate(tempId), delay)
      );
      setPending(true);
    },
    [delay, flushCreate, setPending]
  );

  // --- public API ---------------------------------------------------------

  /**
   * Append a blank draft. Its line-up continues from the highest one in use
   * rather than the row count, so it stays unique after rows are deleted or
   * renumbered.
   */
  const addItem = useCallback(() => {
    const nextSeq =
      itemsRef.current.reduce(
        (max, item) => Math.max(max, Number(item.seq) || 0),
        0
      ) + 1;
    const draft = {
      tempId: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      description: "",
      notes: "",
      type: "",
      is_completed: false,
      read: false,
      is_reviewed: false,
      ai_time_estimate: null,
      seq: nextSeq,
    };
    commit((prev) => [...prev, draft]);
    return draft;
  }, [commit]);

  /**
   * Update one row by its key (`id` for saved rows, `tempId` for drafts). The
   * key is explicit because the old "there is exactly one pending item" model
   * misrouted edits whenever a second draft existed.
   */
  const updateItem = useCallback(
    (key, field, value) => {
      const current = itemsRef.current.find((item) => itemKey(item) === key);
      if (!current) return;

      commit((prev) =>
        prev.map((item) =>
          itemKey(item) === key ? { ...item, [field]: value } : item
        )
      );

      if (current.id) {
        scheduleUpdate(current.id, field, value);
        return;
      }

      const { tempId } = current;
      if (creatingRef.current.has(tempId)) {
        // The POST is already out; queue this for replay once we have an id.
        const queued = postCreateEditsRef.current.get(tempId) || {};
        queued[field] = value;
        postCreateEditsRef.current.set(tempId, queued);
        return;
      }

      // A draft only becomes a row once it has a description — the API rejects
      // an empty one, and the failed create would make the row disappear.
      const description = field === "description" ? value : current.description;
      if (String(description || "").trim()) scheduleCreate(tempId);
    },
    [commit, scheduleCreate, scheduleUpdate]
  );

  /**
   * Clear the Type of every row whose value is no longer valid under `category`.
   * Called when the parent task's category changes, because the API rejects a
   * type that doesn't belong to the task's category.
   */
  const clearInvalidTypes = useCallback(
    (category) => {
      const valid = typesForCategory(category);
      itemsRef.current
        .filter((item) => item.type && !valid.includes(item.type))
        .forEach((item) => updateItem(itemKey(item), "type", ""));
    },
    [updateItem]
  );

  const removeItem = useCallback(
    async (key) => {
      const item = itemsRef.current.find((i) => itemKey(i) === key);
      if (!item) return;

      if (!item.id) {
        const timer = createTimersRef.current.get(item.tempId);
        if (timer) clearTimeout(timer);
        createTimersRef.current.delete(item.tempId);
        postCreateEditsRef.current.delete(item.tempId);
        commit((prev) => prev.filter((i) => i.tempId !== item.tempId));
        return;
      }

      const entry = updateTimersRef.current.get(item.id);
      if (entry) clearTimeout(entry.timer);
      updateTimersRef.current.delete(item.id);

      try {
        await api.delete(`action-items/${item.id}/`);
        commit((prev) => prev.filter((i) => i.id !== item.id));
      } catch (error) {
        console.error("Error deleting action item:", error);
      }
    },
    [commit]
  );

  /** Replace the list wholesale (initial load, workspace switch). */
  const setItems = useCallback(
    (next) => commit(typeof next === "function" ? next : processActionItems(next)),
    [commit]
  );

  /** Reload the task's items from the API (after a template import, say). */
  const reload = useCallback(async () => {
    const { taskId: task, workspaceId: ws } = cfgRef.current;
    if (!task) return;
    try {
      const response = await api.get(`tasks/${task}/action-items/`, {
        params: { workspace_id: ws },
      });
      commit(processActionItems(response.data?.payload || []));
    } catch (error) {
      console.error("Error fetching action items:", error);
    }
  }, [commit]);

  // Push out everything still queued when the surface goes away.
  useEffect(
    () => () => {
      const drafts = [...createTimersRef.current.keys()];
      createTimersRef.current.forEach((timer) => clearTimeout(timer));
      createTimersRef.current.clear();
      drafts.forEach((tempId) => flushCreate(tempId));

      [...updateTimersRef.current.keys()].forEach((id) => flushUpdate(id));
    },
    [flushCreate, flushUpdate]
  );

  return useMemo(
    () => ({
      items,
      setItems,
      addItem,
      updateItem,
      removeItem,
      reload,
      clearInvalidTypes,
    }),
    [items, setItems, addItem, updateItem, removeItem, reload, clearInvalidTypes]
  );
}
